/**
 * @file NotFoundRow.tsx
 * @description Ячейки строки журнала «Не найдено» (phase19.16, партия
 * «SEO-техника», v0.19.65; SB148, SB55; макет uiux-seo-tech.md §3.1):
 * адрес, кнопка сортировки в шапке, статус, кнопка действия строки.
 * Из них `not-found-columns.tsx` собирает колонки `ResponsiveList`: с 1024 —
 * таблица «Адрес · Посетители ↓ · Поисковики ⓘ · Последний раз · Статус ·
 * [действие] · ⋯», ниже — карточка «адрес / 42 посетителя · 7 поисковиков /
 * последний раз сегодня · статус / [Переадресовать]».
 *
 * Строка «Прочие адреса сверх 1000 в день» — всегда последняя, без действий.
 * Кнопка в строке видна (а не спрятана в «⋯»): «Переадресовать» у новой
 * строки, «Изменить» у недействующего правила; без права настроек её нет.
 * На 1280 заголовки «Посетители» и «Поисковики» — кнопки сортировки.
 */

import type { NotFoundJournalRowDto, NotFoundSort } from "@chaiberi/shared";
import { ArrowDown, Info } from "lucide-react";
import { Fragment, type ReactNode } from "react";

import { Button } from "../../ui/button";
import { StatusPill } from "../../ui/StatusPill";
import { OTHER_HINT, notFoundRowAction, notFoundStatusPill } from "../../../lib/site-seo/not-found-view";

/** Строка списка: адрес журнала или итог «прочие». */
export type NotFoundListRow = { kind: "path"; row: NotFoundJournalRowDto } | { kind: "other"; visitors: number; bots: number; lastDay: string };

export interface NotFoundRowHandlers {
  canEdit: boolean;
  /** Последний день периода (`YYYY-MM-DD`) — «сегодня/вчера». */
  today: string;
  sort: NotFoundSort;
  onSort: (sort: NotFoundSort) => void;
  onRedirect: (row: NotFoundJournalRowDto) => void;
  onEdit: (row: NotFoundJournalRowDto) => void;
  onProbe: (path: string) => void;
  onHide: (row: NotFoundJournalRowDto) => void;
  onUnhide: (row: NotFoundJournalRowDto) => void;
  /** Адрес сайта для «Открыть на сайте ↗»; `null` — пункта нет. */
  siteUrl: string | null;
}

const OTHER_TITLE = "Прочие адреса сверх 1000 в день";

/** Кнопка сортировки в шапке таблицы. */
export function SortHeader({ label, value, h, hint }: { label: string; value: NotFoundSort; h: NotFoundRowHandlers; hint?: string }) {
  const active = h.sort === value;
  return (
    <span className="inline-flex items-center gap-1">
      <Button variant="ghost" size="sm" className="-mx-2 gap-1 px-2" aria-pressed={active} onClick={() => h.onSort(value)}>
        {label}
        {active ? <ArrowDown aria-label="по убыванию" className="size-3.5" /> : null}
      </Button>
      {hint ? <Info role="img" aria-label={hint} className="size-3.5 text-on-surface-variant"><title>{hint}</title></Info> : null}
    </span>
  );
}

/** Места переноса после «/» и «-»: длинный адрес рвётся по частям, а не посреди слова (как `SiteFileLink`). */
function withPathBreaks(path: string): ReactNode[] {
  return path.split(/(?<=[/-])/u).map((part, index) => (
    <Fragment key={index}>
      {part}
      <wbr />
    </Fragment>
  ));
}

/** Адрес: моноширинный, перенос по «/», в таблице не больше 3 строк, полный — во всплывающей подписи. */
export function PathCell({ item }: { item: NotFoundListRow }) {
  if (item.kind === "other") {
    return (
      <span className="inline-flex items-center gap-1 text-on-surface-variant">
        {OTHER_TITLE}
        <Info role="img" aria-label={OTHER_HINT} className="size-3.5 shrink-0"><title>{OTHER_HINT}</title></Info>
      </span>
    );
  }
  return (
    <span title={item.row.display} className="line-clamp-3 break-words font-mono text-on-surface">
      {withPathBreaks(item.row.display)}
    </span>
  );
}

/** Кнопка строки; `wide` — карточка до 1024 (во всю ширину, подпись полнее). */
export function ActionButton({ item, h, wide = false }: { item: NotFoundListRow; h: NotFoundRowHandlers; wide?: boolean }) {
  if (item.kind === "other" || !h.canEdit) return null;
  const action = notFoundRowAction(item.row);
  if (action === "redirect") {
    return (
      <Button variant="outline" className={wide ? "flex-1" : undefined} onClick={() => h.onRedirect(item.row)}>
        Переадресовать
      </Button>
    );
  }
  if (action === "edit") {
    return (
      <Button variant="outline" className={wide ? "flex-1" : undefined} onClick={() => h.onEdit(item.row)}>
        {wide ? "Изменить переадресацию" : "Изменить"}
      </Button>
    );
  }
  return null;
}

export function StatusCell({ item }: { item: NotFoundListRow }) {
  if (item.kind === "other") return null;
  const pill = notFoundStatusPill(item.row);
  if (!pill) return <span aria-label="нет статуса">—</span>;
  // Пилюля в одну строку: длинный адрес цели обрезается, полный — во всплывающей подписи.
  return (
    <span title={pill.label} className="inline-flex max-w-full min-w-0">
      <StatusPill tone={pill.tone} className="max-w-full min-w-0 overflow-hidden whitespace-nowrap">
        <span className="min-w-0 truncate">{pill.label}</span>
      </StatusPill>
    </span>
  );
}
